import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Save, Plus, Trash2, FileText } from 'lucide-react';

export default function AgreementSettingsPage() {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/agreements/template').then(res => setTemplate(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/agreements/template', template);
      toast.success('Agreement template saved');
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addTerm = () => setTemplate(t => ({ ...t, terms: [...(t.terms || []), ''] }));
  const updateTerm = (i, val) => setTemplate(t => ({ ...t, terms: t.terms.map((v, idx) => idx === i ? val : v) }));
  const removeTerm = (i) => setTemplate(t => ({ ...t, terms: t.terms.filter((_, idx) => idx !== i) }));

  if (loading || !template) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="agreement-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-brand-text-primary tracking-tight">Agreement Template</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Customize the agreement document template</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-brand-primary text-white rounded-lg" data-testid="save-template-btn">
          <Save size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save Template'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Details */}
        <Card className="border border-brand-border bg-white">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-lg font-medium flex items-center gap-2"><FileText size={18} className="text-brand-primary" /> Company Details</CardTitle></CardHeader>
          <CardContent className="space-y-4 p-6 pt-2">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input value={template.company_name || ''} onChange={e => setTemplate({...template, company_name: e.target.value})} className="bg-white border-brand-border rounded-lg" data-testid="tpl-company-name" />
            </div>
            <div className="space-y-2">
              <Label>CIN Number</Label>
              <Input value={template.company_cin || ''} onChange={e => setTemplate({...template, company_cin: e.target.value})} placeholder="U62099WR2026PTC292718" className="bg-white border-brand-border rounded-lg font-mono" data-testid="tpl-company-cin" />
            </div>
            <div className="space-y-2">
              <Label>Company Address / Website</Label>
              <Input value={template.company_address || ''} onChange={e => setTemplate({...template, company_address: e.target.value})} className="bg-white border-brand-border rounded-lg" data-testid="tpl-company-address" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Support Email</Label>
                <Input value={template.support_email || ''} onChange={e => setTemplate({...template, support_email: e.target.value})} className="bg-white border-brand-border rounded-lg" data-testid="tpl-support-email" />
              </div>
              <div className="space-y-2">
                <Label>Support Phone</Label>
                <Input value={template.support_phone || ''} onChange={e => setTemplate({...template, support_phone: e.target.value})} className="bg-white border-brand-border rounded-lg" data-testid="tpl-support-phone" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profit Sharing */}
        <Card className="border border-brand-border bg-white">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-lg font-medium">Profit Sharing %</CardTitle></CardHeader>
          <CardContent className="p-6 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Investor %</Label>
                <Input type="number" value={template.investor_pct || ''} onChange={e => setTemplate({...template, investor_pct: parseFloat(e.target.value) || 0})} className="bg-white border-brand-border rounded-lg" data-testid="tpl-investor-pct" />
              </div>
              <div className="space-y-2">
                <Label>Trader %</Label>
                <Input type="number" value={template.trader_pct || ''} onChange={e => setTemplate({...template, trader_pct: parseFloat(e.target.value) || 0})} className="bg-white border-brand-border rounded-lg" data-testid="tpl-trader-pct" />
              </div>
              <div className="space-y-2">
                <Label>Company %</Label>
                <Input type="number" value={template.company_pct || ''} onChange={e => setTemplate({...template, company_pct: parseFloat(e.target.value) || 0})} className="bg-white border-brand-border rounded-lg" data-testid="tpl-company-pct" />
              </div>
              <div className="space-y-2">
                <Label>Manager %</Label>
                <Input type="number" value={template.manager_pct || ''} onChange={e => setTemplate({...template, manager_pct: parseFloat(e.target.value) || 0})} className="bg-white border-brand-border rounded-lg" data-testid="tpl-manager-pct" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Terms & Conditions */}
      <Card className="border border-brand-border bg-white">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg font-medium">Terms & Conditions</CardTitle>
          <Button variant="outline" size="sm" onClick={addTerm} className="rounded-lg text-xs" data-testid="add-term-btn"><Plus size={14} className="mr-1" /> Add Clause</Button>
        </CardHeader>
        <CardContent className="p-6 pt-2 space-y-3">
          {template.terms?.map((term, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-xs text-brand-text-secondary mt-2.5 min-w-[20px]">{i + 1}.</span>
              <Textarea value={term} onChange={e => updateTerm(i, e.target.value)} rows={2} className="bg-white border-brand-border rounded-lg text-sm flex-1" data-testid={`term-${i}`} />
              <Button variant="ghost" size="icon" onClick={() => removeTerm(i)} className="h-8 w-8 text-brand-error shrink-0 mt-1"><Trash2 size={14} /></Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Risk Disclaimer */}
      <Card className="border border-brand-border bg-white">
        <CardHeader className="pb-2"><CardTitle className="font-heading text-lg font-medium text-brand-error">Risk Disclaimer</CardTitle></CardHeader>
        <CardContent className="p-6 pt-2">
          <Textarea value={template.risk_disclaimer || ''} onChange={e => setTemplate({...template, risk_disclaimer: e.target.value})} rows={4} className="bg-white border-brand-border rounded-lg text-sm" data-testid="tpl-risk-disclaimer" />
        </CardContent>
      </Card>
    </div>
  );
}
